/**
 * Urensaldo Calculator — overurenpotje mutatie-logica (CAO Schoonmaak)
 *
 * Pure helpers zonder DB-toegang. Gebruikt door NMBRS-export preview om
 * voorgestelde potje-mutaties te berekenen en door de team-app
 * om het actuele saldo af te leiden uit de mutaties-stream.
 *
 * Regels:
 * - Vast contract: contracturen gegarandeerd; meerwerk (base boven contracturen)
 *     wordt uitbetaald tot de drempel, daarboven → deposit. Nooit withdrawal.
 * - Jaaruren: vast maandsalaris op de contracturen (jaarnorm/12), plus- en
 *     minuren lopen in het jaarsaldo (jaaruren-stand.ts), NIET in het potje.
 *     Geen mutaties; uitbetaling van plusuren volgt bij de jaarafrekening.
 * - Vast-salaris: vast maandsalaris zonder urenregistratie; meerwerk zit in het
 *     salaris (contract art 4). Altijd de contracturen, nooit een mutatie.
 * - Min-max / 0-uren: baseHours = gewerkt + verlof + ziekte.
 *     baseHours > drempel → deposit (baseHours - drempel), paid = drempel.
 *     baseHours < drempel & potje > 0 → withdrawal tot drempel, gecapt op potje-saldo.
 *     Anders: paid = baseHours, geen mutatie.
 * - ZZP/DGA: geen mutaties, paid = gewerkt.
 */

export type PotjeSource = 'nmbrs_export' | 'manual_correction' | 'migration' | 'end_of_contract'
  | 'jaaruren_uitbetaling' | 'jaaruren_afrekening';

// Jaaruren-uitbetalingen (cap-overloop/jaarafrekening) lopen via dezelfde
// mutaties-tabel (payroll-run-binding + void bij heropenen) maar horen bij het
// spaarsaldo van jaaruren-stand.ts, NIET bij het overurenpotje.
export const JAARUREN_SOURCES: PotjeSource[] = ['jaaruren_uitbetaling', 'jaaruren_afrekening'];

export interface Mutation {
  id?: string;
  staff_id?: string;
  hours: number;
  source: PotjeSource;
  reason: string;
  voided_at?: string | null;
  period_label?: string | null;
  created_at?: string;
}

export interface UrensaldoState {
  potjeBefore: number;
  suggestedDeposit: number;      // altijd >= 0
  suggestedWithdrawal: number;   // altijd >= 0 (wordt negatief opgeslagen in mutations.hours)
  paidHours: number;             // uit te betalen uren op loonstrook
  potjeAfter: number;
}

export interface UrensaldoInput {
  contractType: string;
  contractUrenPerMaand: number | null;  // voor vast en jaaruren
  minUrenPerMaand: number | null;        // alleen voor min-max (= min_uren_per_week × 52/12)
  workedHours: number;
  leaveHours: number;
  sickHours: number;
  drempel: number;                       // staff.maand_norm ?? 173
  potjeBefore: number;
}

/**
 * Som van niet-voided potje-mutaties = actueel potje-saldo.
 * Jaaruren-uitbetalingen tellen niet mee (die verlagen het spaarsaldo).
 */
export function calculatePotjeSaldo(mutations: Mutation[]): number {
  return mutations
    .filter(m => !m.voided_at && !JAARUREN_SOURCES.includes(m.source))
    .reduce((sum, m) => sum + Number(m.hours), 0);
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Bereken voorgestelde paid hours + mutaties voor één medewerker in één periode.
 */
export function calculateUrensaldoState(input: UrensaldoInput): UrensaldoState {
  const { contractType, contractUrenPerMaand, minUrenPerMaand, workedHours, leaveHours, sickHours, drempel, potjeBefore } = input;

  // Jaaruren: vast maandsalaris op de contracturen; plus/min loopt in het
  // jaarsaldo (spaarurensysteem art 8 B-deel cao), niet in het potje.
  // Vast-salaris: idem contracturen, meerwerk in het salaris begrepen.
  if (contractType === 'jaaruren' || contractType === 'vast-salaris') {
    return {
      potjeBefore,
      suggestedDeposit: 0,
      suggestedWithdrawal: 0,
      paidHours: round2(contractUrenPerMaand ?? 0),
      potjeAfter: potjeBefore,
    };
  }

  // ZZP/DGA: geen potje-mechanisme
  if (contractType === 'zzp' || contractType === 'dga') {
    return {
      potjeBefore,
      suggestedDeposit: 0,
      suggestedWithdrawal: 0,
      paidHours: round2(workedHours),
      potjeAfter: potjeBefore,
    };
  }

  // Vast: contracturen zijn gegarandeerd (verlof/ziekte doorbetaald binnen
  // contract). Meerwerk boven de contracturen wordt uitbetaald tot de drempel;
  // alles daarboven gaat als deposit het potje in. Nooit withdrawal.
  if (contractType === 'vast') {
    const contractUren = contractUrenPerMaand ?? 0;
    const base = Math.max(workedHours + leaveHours + sickHours, contractUren);
    const payCap = Math.max(drempel, contractUren);
    const deposit = base > payCap ? round2(base - payCap) : 0;
    return {
      potjeBefore,
      suggestedDeposit: deposit,
      suggestedWithdrawal: 0,
      paidHours: round2(Math.min(base, payCap)),
      potjeAfter: round2(potjeBefore + deposit),
    };
  }

  // Min-max / 0-uren / student: topup-regel.
  // Min-max heeft een contractueel minimum (werkgever moet min-uren betalen
  // ongeacht gewerkte uren). Dat minimum dient als floor voor baseHours:
  // de werkgever "eet" het verschil tussen gewerkt en min-uren, potje komt
  // pas aan bod voor de rest van de topup richting de drempel.
  const rawBase = workedHours + leaveHours + sickHours;
  const floor = contractType === 'min-max' && minUrenPerMaand ? minUrenPerMaand : 0;
  const baseHours = Math.max(rawBase, floor);

  if (baseHours > drempel) {
    const deposit = round2(baseHours - drempel);
    return {
      potjeBefore,
      suggestedDeposit: deposit,
      suggestedWithdrawal: 0,
      paidHours: round2(drempel),
      potjeAfter: round2(potjeBefore + deposit),
    };
  }

  if (baseHours < drempel && potjeBefore > 0) {
    const gap = drempel - baseHours;
    const withdrawal = round2(Math.min(gap, potjeBefore));
    return {
      potjeBefore,
      suggestedDeposit: 0,
      suggestedWithdrawal: withdrawal,
      paidHours: round2(baseHours + withdrawal),
      potjeAfter: round2(potjeBefore - withdrawal),
    };
  }

  // baseHours == drempel, of baseHours < drempel zonder potje
  return {
    potjeBefore,
    suggestedDeposit: 0,
    suggestedWithdrawal: 0,
    paidHours: round2(baseHours),
    potjeAfter: potjeBefore,
  };
}
